import React, { useCallback, useMemo } from 'react';
import { Combobox, Flex, TextV2 } from '@withjoy/joykit';
import { Command } from '@withjoy/joykit/components/Command';
import type { CurrencySelectorV2Props } from './CurrencySelectorV2.types';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import type { CurrencyMetadata } from './currencyMetadata';
import { useCurrencyMetadata } from './currencyMetadata';
import { formatCurrency } from './CurrencySelectorV2.utils.internal';
import { useTranslation } from '@shared/core';
import { WebGlobe } from '@withjoy/joykit/icons';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import { CURRENCY_BUTTON_PROPS, GROUP_HEADING_OVERRIDES, BUTTON_MODE_GROUP_OVERRIDES } from './CurrencySelectorV2.styles';

const DEFAULT_CURRENCY_CODES: ReadonlyArray<CurrencyCodeType> = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY'];
const EMPTY_CODES: ReadonlyArray<CurrencyCodeType> = [];

export function CurrencySelectorV2<C extends CurrencyCodeType = CurrencyCodeType>({
  value,
  onChange,
  disabled,
  placeholder,
  searchable = true,
  mode,
  currencies,
  hideFlag = false,
}: CurrencySelectorV2Props<C>) {
  const { tq } = useTranslation('currencySelectorV2');

  // Only fall back to defaults when the entire currencies prop is omitted.
  // If currencies is provided without popularCurrencyCodes, treat popular as empty
  // so the caller's allCurrencyCodes list is respected without injected defaults.
  const popularCodes = useMemo((): ReadonlyArray<CurrencyCodeType> => (currencies ? (currencies.popularCurrencyCodes ?? []) : DEFAULT_CURRENCY_CODES), [currencies]);

  const allCodes = currencies?.allCurrencyCodes ?? EMPTY_CODES;

  const resolvedPopular = useCurrencyMetadata(popularCodes);
  const resolvedAll = useCurrencyMetadata(allCodes);

  const isButtonMode = mode === 'button';

  const formatOptions = useMemo(() => ({ hideFlag }), [hideFlag]);

  // displayValue searches across both groups
  const displayValue = useMemo(() => {
    if (!value) return '';
    const currency = resolvedPopular.find(c => c.code === value) ?? resolvedAll.find(c => c.code === value);
    if (!currency) return '';
    if (isButtonMode) {
      const symbol = getCurrencySymbolFromCurrencyCode(currency.code);
      return `${symbol} ${currency.code}`;
    }
    const formatted = formatCurrency(currency.code, currency.name, currency.flag, formatOptions);
    return formatted.inputDisplay;
  }, [value, resolvedPopular, resolvedAll, isButtonMode, formatOptions]);

  const renderCurrencyItem = useCallback(
    (currency: CurrencyMetadata) => {
      const formatted = formatCurrency(currency.code, currency.name, currency.flag, formatOptions);
      return (
        <Command.Item
          key={currency.code}
          value={currency.code}
          keywords={[currency.name, currency.code, formatted.symbol]}
        >
          <Flex
            flexDirection="column"
            gap={0}
          >
            <TextV2 typographyVariant={isButtonMode ? 'button1' : 'body1'}>{formatted.popoverDisplayLine1}</TextV2>
            <TextV2
              typographyVariant={isButtonMode ? 'label1' : 'body2'}
              color={isButtonMode ? 'mono10' : 'mono7'}
              marginTop={isButtonMode ? { _: 0, sm2: 1 } : 0}
            >
              {formatted.popoverDisplayLine2}
            </TextV2>
          </Flex>
        </Command.Item>
      );
    },
    [isButtonMode, formatOptions],
  );

  const { popularItems, allItems } = useMemo(() => {
    return {
      popularItems: resolvedPopular.map(renderCurrencyItem),
      allItems: resolvedAll.map(renderCurrencyItem),
    };
  }, [resolvedPopular, resolvedAll, renderCurrencyItem]);

  const groupOverrides = isButtonMode ? BUTTON_MODE_GROUP_OVERRIDES : GROUP_HEADING_OVERRIDES;

  const currencyContent = (
    <>
      <Command.Group
        heading={tq`Popular Currencies`}
        overrides={groupOverrides}
      >
        {popularItems}
      </Command.Group>
      {allItems.length > 0 && (
        <Command.Group
          heading={tq`All Currencies`}
          overrides={groupOverrides}
        >
          {allItems}
        </Command.Group>
      )}
    </>
  );

  const handleValueChange = (currencyCode: string) => {
    onChange(currencyCode as C);
  };

  const comboboxProps = {
    value,
    onValueChange: handleValueChange,
    disabled,
    placeholder: placeholder || tq`Select currency...`,
    searchPlaceholder: tq`Search currencies...`,
    emptyMessage: tq`No currencies found`,
    displayValue,
    searchable,
  };

  if (isButtonMode) {
    return (
      <Combobox
        mode="button"
        buttonProps={CURRENCY_BUTTON_PROPS}
        buttonModeMobileIcon={
          <WebGlobe
            size={24}
            color="mono14"
          />
        }
        dialogTitle={tq`Choose Currency`}
        {...comboboxProps}
      >
        {currencyContent}
      </Combobox>
    );
  }

  return <Combobox {...comboboxProps}>{currencyContent}</Combobox>;
}

CurrencySelectorV2.displayName = 'CurrencySelectorV2';
